import {
  useEffect,
  useState,
  ReactNode,
} from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { getFirebaseAuth, initializeAppCheckAfterLogin } from "@/lib/firebase";
import { FEATURES } from "@/config";
import { AuthContext } from "./auth-context";
import {
  signInWithEmail,
  createAccountWithEmail,
  signOutUser,
  sendPasswordReset,
  initializeAuthPersistence,
} from "../services/auth-service";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const isAuthEnabled = FEATURES.authEnabled;

  useEffect(() => {
    // If auth is disabled, skip Firebase auth initialization entirely
    if (!isAuthEnabled) {
      setIsAuthLoading(false);
      setIsAuthReady(true);
      return;
    }

    // Only initialize Firebase Auth when auth is enabled
    let unregisterAuthObserver: (() => void) | undefined;

    const initAuth = async () => {
      try {
        // Initialize session persistence
        await initializeAuthPersistence();

        // Listen to Firebase auth state changes
        const auth = getFirebaseAuth();
        unregisterAuthObserver = onAuthStateChanged(auth, async (user) => {
          // Initialize App Check after successful login (before Firestore/Storage access)
          // IMPORTANT: Await this to prevent race conditions with Firestore access
          if (user) {
            await initializeAppCheckAfterLogin();
          }
          setFirebaseUser(user);
          setIsAuthLoading(false);
          setIsAuthReady(true);
        });
      } catch (error) {
        console.error("Failed to initialize Firebase Auth:", error);
        setIsAuthLoading(false);
        setIsAuthReady(true);
      }
    };

    initAuth();

    return () => {
      if (unregisterAuthObserver) {
        unregisterAuthObserver();
      }
    };
  }, [isAuthEnabled]);

  const signIn = async (email: string, password: string): Promise<void> => {
    await signInWithEmail(email, password);
  };

  const signUp = async (email: string, password: string): Promise<void> => {
    await createAccountWithEmail(email, password);
  };

  const signOut = async (): Promise<void> => {
    // Clear sessionStorage FIRST to prevent race conditions with stale data
    sessionStorage.clear();
    await signOutUser();
  };

  const handlePasswordReset = async (email: string): Promise<void> => {
    await sendPasswordReset(email);
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        isAuthEnabled,
        isAuthLoading,
        isAuthReady,
        signIn,
        signUp,
        signOut,
        sendPasswordReset: handlePasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
